/**
 * DonateLayout is used most journeys in this app (i.e. donate single, donate monthly & payin journeys)
 * This layout relies on *cart data* for images/styling - it can only be used in contexts where we have cart data
 */
import { ThemeProvider, crTheme, LogoLinked } from '@comicrelief/component-library';

// Is it actually worth using the service to populate the footer?
// See: https://github.com/comicrelief/serverless-content/blob/master/src/Action/Footer.action.js

import { useLocation } from 'react-router-dom';
import snakeCase from 'lodash/snakeCase';
import { useCartContext } from '@/services/cart/CartContext';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';
import Banner from '@/components/banner/Banner';
import FundraisingRegulatorRow from '../FundraisingRegulatorRow/FundraisingRegulatorRow';
import DataAttributeWrapper from '@/components/DataAttributeWrapper/DataAttributeWrapper';
import FooterCards from '@/components/footerCards/FooterCards';
import defaultCards from '@/components/footerCards/defaultCards';

import Footer from '@/components/footer/Footer';
import SecureTextAndIcon from '@/components/common/SecureTextAndIcon';
import {
  Main,
  SmallAndMediumHeader,
  DesktopLogo,
  OuterContainer,
  InnerContainer,
  BackgroundImage,
  ContentWrapper,
  SecureTextAndIconDesktopWrapper
} from './DonateLayout.style';

function DonateLayout({ children }: React.PropsWithChildren) {
  const {
    getCartJourneyHeader, getCartFooterCards, getCartBannerHtml, getCartFinancialAttribution, getDesktopBackgroundColour, cartId
  } = useCartContext();

  const { selectedJourney, journeyOptions } = useJourneyOptions();
  const bannerContent = getCartBannerHtml();

  // Pull various bits of content from active cart, if available:
  const cartHeader = getCartJourneyHeader();
  const footerCardsConfig = getCartFooterCards();

  // If this cart doesn't have its own FooterCards, use our
  // hardcoded defaults in the same format from the codebase;
  const theseFooterCards = footerCardsConfig ?? defaultCards;

  const campaignCode =  getCartFinancialAttribution()?.campaignCode ?? null;
  const isJourneySelectPage = useLocation().pathname === '/';
  let desktopBgColour = getDesktopBackgroundColour();
  desktopBgColour = desktopBgColour ? snakeCase(desktopBgColour) : null;

  return (
    <ThemeProvider theme={crTheme}>
      <Main
        role="main"
        id="main-content"
      >
        <DataAttributeWrapper
          cartId={cartId ?? null}
          campaignCode={campaignCode}
          selectedJourney={selectedJourney}
          journeyOptions={journeyOptions}
        >

          <OuterContainer $desktopbackgroundcolour={desktopBgColour}>

            { !desktopBgColour && <BackgroundImage src={cartHeader?.desktopUrl} alt={cartHeader?.alt ?? 'Background img'} />}

            <DesktopLogo>
              <LogoLinked url="https://www.comicrelief.com" animateRotate />
            </DesktopLogo>

            <InnerContainer>

              <SmallAndMediumHeader $isJourneySelectPage={isJourneySelectPage}>
                <LogoLinked url="https://www.comicrelief.com" animateRotate />
                <SecureTextAndIcon $mobile />
              </SmallAndMediumHeader>

              {bannerContent && <Banner bannerContent={bannerContent} />}

              <ContentWrapper>
                {children}
              </ContentWrapper>
            </InnerContainer>

            <SecureTextAndIconDesktopWrapper>
              <SecureTextAndIcon />
            </SecureTextAndIconDesktopWrapper>
          </OuterContainer>

          {theseFooterCards && (
            <FooterCards
              title={theseFooterCards.title}
              cardCopy={theseFooterCards.cardCopy}
            />
          )}

          <FundraisingRegulatorRow />
        </DataAttributeWrapper>

      </Main>

      <Footer />
    </ThemeProvider>
  );
}

export default DonateLayout;
